/** Journal entries: filters on top, results below (M03-04). */
import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { api } from '../../lib/api';
import { useAuth } from '../../lib/auth';
import { amount } from '../../lib/money';
import { shortDate } from '../../lib/format';
import { Alert, Button, Card, EmptyState, FilterBar, Money, PageHeader, Skeleton, StatusChip, TableWrap, Td, Th, filterClass } from '../../components/ui';
import { SOURCE_LABEL, STATUS_LABEL, STATUS_TONE, type JournalSummary } from './journalTypes';

interface Page {
  data: JournalSummary[];
  page: number;
  pageSize: number;
  total: number;
}

export function JournalsPage(): JSX.Element {
  const { can } = useAuth();
  const [search] = useSearchParams();
  const [status, setStatus] = useState(search.get('status') ?? '');
  const [source, setSource] = useState('');
  const [q, setQ] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const params = new URLSearchParams({ page: String(page), pageSize: '50' });
  if (status) params.set('status', status);
  if (source) params.set('source', source);
  if (q.trim()) params.set('q', q.trim());
  if (dateFrom) params.set('dateFrom', dateFrom);
  if (dateTo) params.set('dateTo', dateTo);

  const query = useQuery({
    queryKey: ['journals', params.toString()],
    queryFn: () => api<Page>(`/journals?${params.toString()}`),
    placeholderData: keepPreviousData,
  });

  const pages = query.data ? Math.max(1, Math.ceil(query.data.total / query.data.pageSize)) : 1;

  return (
    <>
      <PageHeader
        title="Journal Entries"
        description="Every posting in the general ledger. Posted entries never change — they are voided by a reversing entry."
        actions={
          can('journals.create') && (
            <Link to="/journals/new">
              <Button>
                <Plus aria-hidden="true" className="h-4 w-4" /> New journal
              </Button>
            </Link>
          )
        }
      />
      <FilterBar>
        <input className={`${filterClass} w-56`} placeholder="Number, narration or reference" value={q} onChange={(e) => { setQ(e.target.value); setPage(1); }} aria-label="Search" />
        <select className={filterClass} value={status} onChange={(e) => { setStatus(e.target.value); setPage(1); }} aria-label="Status">
          <option value="">All statuses</option>
          {Object.entries(STATUS_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <select className={filterClass} value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} aria-label="Source">
          <option value="">All sources</option>
          {Object.entries(SOURCE_LABEL).map(([k, v]) => (
            <option key={k} value={k}>{v}</option>
          ))}
        </select>
        <input type="date" className={filterClass} value={dateFrom} onChange={(e) => { setDateFrom(e.target.value); setPage(1); }} aria-label="From date" />
        <input type="date" className={filterClass} value={dateTo} onChange={(e) => { setDateTo(e.target.value); setPage(1); }} aria-label="To date" />
      </FilterBar>
      <Card>
        {query.isError && <Alert tone="critical">Could not load journal entries.</Alert>}
        {query.isLoading && <Skeleton className="h-48 w-full" />}
        {query.data && query.data.data.length === 0 && (
          <EmptyState title="No journal entries" description="Nothing matches these filters yet." />
        )}
        {query.data && query.data.data.length > 0 && (
          <>
            <TableWrap>
              <thead>
                <tr className="border-b border-slate-200 dark:border-slate-800">
                  <Th>Date</Th>
                  <Th>Number</Th>
                  <Th>Source</Th>
                  <Th>Narration</Th>
                  <Th align="right">Amount</Th>
                  <Th>Status</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                {query.data.data.map((e) => (
                  <tr key={e.id} className="hover:bg-slate-50 dark:hover:bg-slate-800/50">
                    <Td className="whitespace-nowrap">{shortDate(e.date)}</Td>
                    <Td className="whitespace-nowrap font-medium">
                      <Link to={`/journals/${e.id}`} className="text-brand-700 hover:underline dark:text-brand-100">
                        {e.number ?? 'Unposted'}
                      </Link>
                    </Td>
                    <Td className="whitespace-nowrap">{SOURCE_LABEL[e.source] ?? e.source}</Td>
                    <Td className="max-w-md truncate">{e.narration || e.reference || '—'}</Td>
                    <Td align="right"><Money>{amount(e.totalDebit)}</Money></Td>
                    <Td><StatusChip tone={STATUS_TONE[e.status]}>{STATUS_LABEL[e.status]}</StatusChip></Td>
                  </tr>
                ))}
              </tbody>
            </TableWrap>
            <div className="mt-4 flex items-center justify-between text-sm text-slate-500">
              <span>{query.data.total} entr{query.data.total === 1 ? 'y' : 'ies'}</span>
              <div className="flex items-center gap-2">
                <Button size="sm" variant="secondary" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
                <span>Page {page} of {pages}</span>
                <Button size="sm" variant="secondary" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>Next</Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </>
  );
}
